import { FullLoginActionButtonProps } from '@msc-b2c/interfaces/types';
import { Button } from '@msc-b2c/shared-components';
import { useMsal } from '@azure/msal-react';
import { useSelector } from 'react-redux';
import { RootState } from '@msc-b2c/shared-utils';
import { getLoginRedirectConfig } from '../../utils/loginUtils';
import { useEffect } from 'react';
import { BrowserAuthError, InteractionStatus } from '@azure/msal-browser';

const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin iframe: accessing window.top throws a SecurityError
    return true;
  }
};

export function FullLoginActionButtonWrapper({
  loginButtonLabel,
  fullLoginAuthenticationExchangePage,
  icon,
  actionType,
  id,
  autoLogin,
  logoutRedirectPage,
}: Readonly<FullLoginActionButtonProps>) {
  const { instance, inProgress } = useMsal();
  const azureConfig = useSelector(
    (state: RootState) => state?.genericConfigs?.configs?.azureB2CConfig
  );

  const handleClick = async () => {
    try {
      if (actionType === 'logout') {
        await logoutRedirect();
      } else if (isInIframe()) {
        await loginPopup();
      } else {
        await loginRedirect();
      }
    } catch (error) {
      if (error instanceof BrowserAuthError && error.errorCode === 'user_cancelled') {
        // User closed the popup window — no action needed
        return;
      }
      console.error(error);
    }
  };

  const loginRedirect = async () => {
    await instance.loginRedirect(
      getLoginRedirectConfig(azureConfig, actionType, fullLoginAuthenticationExchangePage)
    );
  };

  const loginPopup = async () => {
    // For popup flow (iframe/VEC), use current page as redirectUri so MSAL can
    // process the auth response in the popup before it closes.
    const config = getLoginRedirectConfig(
      azureConfig,
      actionType,
      fullLoginAuthenticationExchangePage
    );
    const response = await instance.loginPopup(config);
    if (response) {
      instance.setActiveAccount(response.account);
      window.location.href = fullLoginAuthenticationExchangePage;
    }
  };

  const logoutRedirect = async () => {
    await instance.logoutRedirect({
      postLogoutRedirectUri: logoutRedirectPage || window.location.href,
    });
  };

  useEffect(() => {
    if (autoLogin && inProgress === InteractionStatus.None && !window.azureActionTriggered) {
      window.azureActionTriggered = true;
      setTimeout(handleClick, 100);
    }
  }, [autoLogin, handleClick, inProgress]);

  return !autoLogin ? (
    <Button
      id={id}
      styleOption="button"
      text={loginButtonLabel}
      icon={icon}
      onClick={handleClick}
    />
  ) : null;
}

export default FullLoginActionButtonWrapper;

FullLoginActionButtonWrapper.aemResourceType =
  'b2c-website-foundation/components/mymsc/form/fullloginactionbutton';
